import React, { useState, useEffect, useMemo } from "react";
import { pb } from "../../../helpers/pocketbase_helper";
import InventarioAllocationService from "../../../services/InventarioAllocationService";
import { Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Spinner, Card, CardBody, CardTitle, Row, Col } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/DataTableContainer";
import { generateExcel } from "../../../helpers/solicitudesMaterialesExcelHelper";
import { RepositoryFactory } from "../../../repositories";
import { formatProductoNombre } from "../../../utils/productoInventarioHelper";

const InventarioSolicitudes = () => {
  // Initialize repository
  const solicitudRepository = RepositoryFactory.getRepository('solicitud_inventario');

  // State for table data
  const [solicitudes, setSolicitudes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // State for pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // State for modal
  const [modalAceptar, setModalAceptar] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [cantidadAceptada, setCantidadAceptada] = useState("");
  const [saving, setSaving] = useState(false);

  // State for export
  const [exporting, setExporting] = useState(false);

  // Fetch solicitudes from database
  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const resultList = await pb.collection("solicitud_inventario").getList(pageIndex + 1, pageSize, {
        filter: 'completado = false && activo = true',
        expand: "orden_produccion,producto_inventario,producto_inventario.tinta",
        sort: "-created"
      });

      setSolicitudes(resultList.items);
      setTotalRecords(resultList.total);
    } catch (err) {
      console.error("Error fetching solicitudes:", err);
      setError("Error al cargar las solicitudes: " + err.message);
    }
    setLoading(false);
  };

  // Load data on component mount and when pagination changes
  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize]);

  // Define table columns
  const columns = useMemo(() => [
    {
      header: "Orden",
      accessorKey: "orden_produccion",
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const orden = row.original.expand?.orden_produccion;
        return orden ? orden.numero : "N/A";
      }
    },
    {
      header: "Producto Inventario",
      accessorFn: (row) => {
        const inv = row.expand?.producto_inventario;
        if (inv?.nombre) {
          return inv.nombre;
        } else if (inv?.expand?.tinta) {
          return inv.expand.tinta.nombre || "N/A";
        }
        return "N/A";
      },
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const inv = row.original.expand?.producto_inventario;
        if (inv) {
          return formatProductoNombre(inv);
        }
        return "N/A";
      }
    },
    {
      header: "Medida",
      accessorFn: (row) => {
        const inv = row.expand?.producto_inventario;
        return inv?.texto_medida || null;
      },
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const inv = row.original.expand?.producto_inventario;
        return inv?.texto_medida || "-";
      }
    },
    {
      header: "Cantidad Solicitada",
      accessorKey: "total",
      enableSorting: true,
      enableColumnFilter: true
    },
    {
      header: "Disponible",
      accessorFn: (row) => {
        const inv = row.expand?.producto_inventario;
        if (!inv) return 0;
        const total = inv.total || 0;
        const reservado = inv.reservado || 0;
        // El disponible es total - reservado (lo solicitado es meramente informativo)
        return total - reservado;
      },
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const inv = row.original.expand?.producto_inventario;
        if (!inv) return <span className="text-danger">0 (Sin inventario)</span>;
        
        const total = inv.total || 0;
        const reservado = inv.reservado || 0;
        // El disponible es total - reservado (lo solicitado es meramente informativo)
        const disponible = total - reservado;
        const cantidadSolicitada = row.original.total;
        const canAccept = disponible >= cantidadSolicitada;
        
        return (
          <div>
            <span className={disponible > 0 ? "text-success" : "text-danger"}>
              {disponible}
            </span>
            {!canAccept && (
              <div className="text-danger small">
                (Insuficiente - Necesita: {cantidadSolicitada})
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: "Estado",
      accessorKey: "completado",
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const completado = row.original.completado;
        return (
          <span className={`badge ${completado ? 'bg-success' : 'bg-warning'}`}>
            {completado ? "Completado" : "Pendiente"}
          </span>
        );
      }
    },
    {
      header: "Comentarios",
      accessorKey: "comentario",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const comentario = row.original.comentario;
        if (!comentario || comentario.trim() === '') {
          return <span className="text-muted">-</span>;
        }
        // Show truncated comment with tooltip
        const truncated = comentario.length > 50 ? comentario.substring(0, 50) + '...' : comentario;
        return (
          <span title={comentario} style={{ cursor: 'help' }}>
            {truncated}
          </span>
        );
      }
    },
    {
      header: "Acciones",
      accessorKey: "actions",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const solicitud = row.original;
        const inv = solicitud.expand?.producto_inventario;
        // El disponible es total - reservado (lo solicitado es meramente informativo)
        const disponible = inv ? (inv.total || 0) - (inv.reservado || 0) : 0;
        const canAccept = disponible >= solicitud.total && !solicitud.completado;
        
        return (
          <div className="d-flex gap-1">
            <Button
              color="success"
              size="sm"
              onClick={() => handleOpenAceptar(solicitud)}
              disabled={!canAccept}
              title={!canAccept ? "Material insuficiente o ya completado" : "Aceptar solicitud"}
            >
              <i className="fas fa-check"></i>
            </Button>
          </div>
        );
      }
    }
  ], []);

  // Open accept modal
  const handleOpenAceptar = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setCantidadAceptada(solicitud.total.toString());
    setModalAceptar(true);
  };

  // Accept solicitud
  const handleAceptar = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const cantidad = parseInt(cantidadAceptada);

      if (isNaN(cantidad) || cantidad <= 0) {
        throw new Error("La cantidad debe ser un número mayor a 0");
      }

      // Validate availability
      const inv = selectedSolicitud.expand?.producto_inventario;
      if (!inv) {
        throw new Error("No se encontró el inventario del material");
      }
      
      const total = inv.total || 0;
      const reservado = inv.reservado || 0;
      const solicitado = inv.solicitado || 0;
      // El disponible es total - reservado (lo solicitado es meramente informativo)
      const disponible = total - reservado;
      
      if (disponible < cantidad) {
        throw new Error(`No hay suficiente material disponible. Total: ${total}, Reservado: ${reservado}, Disponible: ${disponible}, Necesita: ${cantidad}`);
      }

      // Call allocation service to accept request
      await InventarioAllocationService.acceptRequest(selectedSolicitud.id, cantidad);

      setSuccess(`Solicitud aceptada: ${cantidad} unidades`);
      setModalAceptar(false);
      setCantidadAceptada("");
      setSelectedSolicitud(null);

      // Refresh data
      fetchData();
    } catch (err) {
      console.error("Error accepting request:", err);
      setError("Error al aceptar solicitud: " + err.message);
    }
    setSaving(false);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalAceptar(false);
    setCantidadAceptada("");
    setSelectedSolicitud(null);
  };

  // Export to Excel
  const handleExportarExcel = async () => {
    setExporting(true);
    setError("");
    setSuccess("");
    try {
      // Obtener todas las solicitudes (sin paginación)
      const solicitudesParaExportar = await solicitudRepository.getSolicitudesParaExportacion();

      if (!solicitudesParaExportar || solicitudesParaExportar.length === 0) {
        setError("No hay solicitudes para exportar");
        return;
      }

      // Generar y descargar Excel
      generateExcel(solicitudesParaExportar);
      setSuccess("Excel exportado correctamente");
    } catch (err) {
      console.error("Error al exportar:", err);
      setError("Error al exportar Excel: " + (err.message || err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs maintitle="Dashboard" title="Inventarios" breadcrumbItem="Solicitudes" />

          {/* Success Alert */}
          {success && (
            <Alert color="success" isOpen={!!success} toggle={() => setSuccess("")} fade={true}>
              <strong>¡Éxito!</strong> {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert color="danger" isOpen={!!error} toggle={() => setError("")} fade={true}>
              <strong>Error:</strong> {error}
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle className="h4 d-flex justify-content-between align-items-center">
                    Solicitudes de Material
                    <div>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleExportarExcel}
                        disabled={exporting || loading}
                        className="me-2"
                      >
                        <i className="fas fa-file-excel me-1"></i>
                        Exportar a Excel
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        title="Recargar tabla"
                        onClick={fetchData}
                      >
                        <i className="fas fa-sync-alt"></i>
                      </Button>
                    </div>
                  </CardTitle>
                  <div className="table-rep-plugin" style={{ overflow: "visible" }}>
                    <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                      {loading ? (
                        <div className="text-center p-4">
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <TableContainer
                          columns={columns}
                          data={solicitudes}
                          isGlobalFilter={false}
                          isPagination={true}
                          isCustomPageSize={true}
                          pageIndex={pageIndex}
                          setPageIndex={setPageIndex}
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          totalRecords={totalRecords}
                          pagination="pagination"
                          paginationWrapper='dataTables_paginate paging_simple_numbers'
                          tableClass="table-bordered table-nowrap dt-responsive w-100 dataTable no-footer dtr-inline"
                        />
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Accept Modal */}
      <Modal isOpen={modalAceptar} toggle={handleCloseModal} size="lg">
        <ModalHeader toggle={handleCloseModal}>
          Aceptar Solicitud
        </ModalHeader>
        <ModalBody>
          {selectedSolicitud && (
            <>
              <div className="mb-3">
                <strong>Orden:</strong> {selectedSolicitud.expand?.orden_produccion?.numero || "N/A"}
              </div>
              <div className="mb-3">
                <strong>Producto Inventario:</strong> {
                  formatProductoNombre(selectedSolicitud.expand?.producto_inventario)
                }
              </div>
              <div className="mb-3">
                <strong>Cantidad Solicitada:</strong> {selectedSolicitud.total}
              </div>
              <div className="mb-3">
                <strong>Total en Inventario:</strong> {selectedSolicitud.expand?.producto_inventario?.total || 0}
              </div>
              <div className="mb-3">
                <strong>Reservado:</strong> {selectedSolicitud.expand?.producto_inventario?.reservado || 0}
              </div>
              <div className="mb-3">
                <strong>Solicitado:</strong> {selectedSolicitud.expand?.producto_inventario?.solicitado || 0}
              </div>
              <div className="mb-3">
                <strong>Disponible:</strong> {
                  (() => {
                    const inv = selectedSolicitud.expand?.producto_inventario;
                    if (!inv) return 0;
                    const total = inv.total || 0;
                    const reservado = inv.reservado || 0;
                    // El disponible es total - reservado (lo solicitado es meramente informativo)
                    return total - reservado;
                  })()
                }
              </div>
              <FormGroup>
                <Label for="cantidadAceptada">Cantidad a Aceptar</Label>
                <Input
                  type="number"
                  name="cantidadAceptada"
                  id="cantidadAceptada"
                  placeholder="Ingrese cantidad"
                  value={cantidadAceptada}
                  onChange={(e) => setCantidadAceptada(e.target.value)}
                  min="1"
                  max={selectedSolicitud.total}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal} disabled={saving}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleAceptar}
            disabled={saving || !cantidadAceptada}
          >
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            {saving ? "Aceptando..." : "Aceptar"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default InventarioSolicitudes;